// More symbols (32×32, id sym_<id>): hex, salt, sigils, bones, food, oddities — plus The Hand (24×48).
// Style matches symbols.ts: 1-px ink outline, light from top-left, readable by silhouette.
// Sigils share a dotted teal glow rim and ice sparkles (see sym_sig_scratch).
// Key: '.' transparent, 'o' ink outline, other chars per sprite.
import { sprite, type SpriteDef } from './types';

export const hex = sprite('sym_hex', { o: 'ink', r: 'red', c: 'coral', b: 'blood', m: 'maroon' }, [
  '................................',
  '................................',
  '...............oo...............',
  '.............oorroo.............',
  '...........oorrrrrroooo.........',
  '........ooorrrrrrrrrrrro........',
  '.......orcrcccbbbcrrrrrro.......',
  '......orcrccbbrrrrbmrrrbbo......',
  '......orrrcbmrrrrrmmcrbbbo......',
  '......orrrbrmmrrrmmrrcbbmo......',
  '......orrrbrrmmmmrmrrcbbbo......',
  '......orrbrrrmrmmrmrrrcbbo......',
  '......orrbrrrrmrrmmrrrcbmo......',
  '.....orrrbrrrmmrrmmmrrcbmbo.....',
  '.....orrrcrrmrmrrmrrmrcmbbo.....',
  '.....orrrrbmmmmmmmmmmmbmbmo.....',
  '.....obbrrcrrrrmrmrrrcmmmo......',
  '......obbrrcrrrrmrrrcmmmbo......',
  '.......oobbbccrrmrccmmmrbo......',
  '........orbbbbccccmmmmmrbo......',
  '........orrbbbbbmmmmbmorbo......',
  '........orrbbmmmmmbbmmobmo......',
  '........orrboooooommbo.oo.......',
  '........orrbo.....orbo..........',
  '........orrbo.....orbo..........',
  '........orbmo.....orbo..........',
  '.........ooo......ocro..........',
  '.................orrbmo.........',
  '..................obmo..........',
  '...................oo...........',
  '................................',
  '................................',
]);

export const salt = sprite(
  'sym_salt',
  { o: 'ink', p: 'paper', s: 'steel', w: 'white', l: 'lilacGrey', d: 'dusk' },
  [
    '................................',
    '................................',
    '................................',
    '..............oooo..............',
    '............oopppsoo............',
    '...........owwwwppsso...........',
    '..........owwwowpopsso..........',
    '.........opwwwwpppssslo.........',
    '.........owwowpopposslo.........',
    '........oppwwppppssslldo........',
    '........oooooooooooooooo........',
    '........oslslslslslslslo........',
    '........oddddddddddddddo........',
    '.........oooooooooooooo.........',
    '........owppwwpppppssslo........',
    '........owppwwpppppssslo........',
    '........owppwwpppppssslo........',
    '........owwswwwwwpsppppo........',
    '........owpwwwswwppwspso........',
    '........owwwwwwwwwwpppso........',
    '........owpwwwwwwwwwppso........',
    '........owwwwwwpwpppppso........',
    '........owwwwwpwwwppppso........',
    '........owwwwpwwwpwpppso........',
    '........owwwppwwwwppppsoooo.....',
    '........owoooooooooooosowwwoo...',
    '.........owwwwwwwwwwwlowwwwwpo..',
    '.........ollllllllllllowwwwwpso.',
    '..........oooooooooooowwwwwwpso.',
    '.....................ooooooooo..',
    '................................',
    '................................',
  ],
);

export const sigLadder = sprite(
  'sym_sig_ladder',
  {
    o: 'ink',
    t: 'teal',
    i: 'ice',
    m: 'mint',
    n: 'neonTeal',
    d: 'deepTeal',
    w: 'white',
    l: 'lemon',
    a: 'amber',
    g: 'gold',
    b: 'brass',
  },
  [
    '................................',
    '.........t.t.t.t.t.t.t..........',
    '........toooooooooooooo.........',
    '....i..tommmmmmmmmmmmmmo........',
    '...iiitomiiiiiiiiiiiiitno.......',
    '....i..omidddddwndddddtnot......',
    '......tomiddddwmmnddddtno..m....',
    '.......omidddwmmmmndddtnot......',
    '......tomiddwmmmmmmnddtno.......',
    '.......omidnnnmmmmnnndtnot......',
    '......tomiddddwmmnddddtno.......',
    '.......omiddddnnnnddddtnot......',
    '......tomidddladdgadddtno.......',
    '.......omidddladdgadddtnot......',
    '......tomidddlabbgadddtno.......',
    '.......omidddladdgadddtnot......',
    '......tomiddladdddgaddtno.......',
    '.......omiddladdddgaddtnot......',
    '......tomiddlabbbbgaddtno.......',
    '.......omiddladdddgaddtnot......',
    '......tomiddladdddgaddtno.......',
    '.......omiddladdddgaddtnot......',
    '......tomiddlabbbbgaddtno.......',
    '....m..omidladdddddgadtnot......',
    '......tomidladdddddgadtno.......',
    '.......omidladdddddgadtnot.i....',
    '......tomidlabbbbbbgadtno.iii...',
    '.......omttttttttttttttnot.i....',
    '........onnnnnnnnnnnnnnot.......',
    '.........oooooooooooooot........',
    '..........t.t.t.t.t.t.t.........',
    '................................',
  ],
);

export const sigWheel = sprite(
  'sym_sig_wheel',
  {
    o: 'ink',
    t: 'teal',
    b: 'blood',
    c: 'coral',
    r: 'red',
    i: 'ice',
    R: 'rust',
    B: 'brick',
    l: 'lemon',
    g: 'gold',
    a: 'brass',
    A: 'amber',
    m: 'maroon',
    n: 'bronze',
    C: 'copper',
  },
  [
    '................................',
    '.............oooooot............',
    '............obccccbot...........',
    '.............ocrrbot............',
    '....i.......tobrrbo.t...........',
    '...iii...t.ooRocboRoot..........',
    '....i...tooRRRobboRRRoo.........',
    '.......toRRRRRroorRRBBBo........',
    '......toRlRRgarrrragBBlBo.......',
    '.......oRRgggarrrragggBBot......',
    '......oRRRggggarragggABBBot.....',
    '.....toRRgggggarraggAAABBo......',
    '....toRRRaaggggrrggAAaaBmmo.....',
    '.....oRRrrraaggaagAaabbbmmot....',
    '....toRRrrrrrraooabbbbbbmmo.....',
    '.....oRlrrrrrraooabbbbbblmot....',
    '....toRRrrraaggaaAAaabbbmmo.....',
    '.....oRRRaagggAbbAAAAaammmot....',
    '......oRRggggAabbaAAAAAmmot.....',
    '.....toRBBggAAabbaAAAAmmmo......',
    '......toBBgAAabbbbaAAAmmo.......',
    '.......oBlBBAabbbbaAmmlmot......',
    '........oBBBBBbbbbmmmmmot...i...',
    '.........ooBBmmmlmmmmoot...iii..',
    '........tonoommmmmmooBo.....i...',
    '.......tonCBooooooooBCBo........',
    '......toooooooooooooooooo.......',
    '.....tonnnnnnnnnnnnnnnnnBo......',
    '......oBBBBBBBBBBBBBBBBBBot.....',
    '.......oooooooooooooooooot......',
    '........t.t.t.t.t.t.t.t.t.......',
    '................................',
  ],
);

export const sigTeeth = sprite(
  'sym_sig_teeth',
  {
    o: 'ink',
    i: 'ice',
    t: 'teal',
    w: 'white',
    r: 'red',
    p: 'paper',
    m: 'mint',
    n: 'neonTeal',
    s: 'steel',
    S: 'salmonPink',
    f: 'flesh',
    c: 'cerise',
    C: 'crimson',
  },
  [
    '................................',
    '............i.......i...........',
    '............i.t.t.t.i...........',
    '.............oooooot............',
    '............owwwwwwot...........',
    '....i......owwwwwwwwot..........',
    '...iii....owwwwwwwwwrot.........',
    '....i....towrwwwwwwwpr..........',
    '..........orwwwwwwwprot.........',
    '.........towwwiimnwppo..........',
    '..........owwwwimnpppot....i....',
    '.........towwmmoontpso....iii...',
    '..........toppmootpso......i....',
    '...........toppttpso............',
    '............toooooo.............',
    '.............t.t.t.......t......',
    '......o.................tot.....',
    '.....owo...t.........t.towot....',
    '....owwpo.oot...t...ootowwpot...',
    '...towwpoowwot.oot.owwoowwpo....',
    '....owwpowwwpoowwoowwwpowwpot...',
    '...toowpowwwpowwwpowwwpowwoo....',
    '..toSfoooowwpowwwpowwwooooSfo...',
    '.toSSfffScowpowwwpowwoSSSSSfco..',
    '..oSfffffffooSooooSooSSSSSffcot.',
    '.tofffffffffSSSSSSSSSSSSfffcco..',
    '..toccfffffffffSSSSffffffccco...',
    '...toccccfffffffffffffccccCo....',
    '....tooocccccccccccccccCooo.....',
    '.....t.tooooCCcccCCCoooo.t......',
    '........t.t.oooooooot.t.........',
    '................................',
  ],
);

export const sigFeast = sprite(
  'sym_sig_feast',
  {
    o: 'ink',
    t: 'teal',
    w: 'white',
    m: 'mint',
    l: 'lilacGrey',
    s: 'steel',
    p: 'paper',
    n: 'neonTeal',
    i: 'ice',
    L: 'lemon',
    a: 'amber',
    b: 'brass',
  },
  [
    '................................',
    '................................',
    '................................',
    '................................',
    '..............t.t.t.............',
    '...........t.oooooot.t..........',
    '..........toowwwwwwooot.........',
    '.........oowwmmmmmmowlot........',
    '........oowmmwwwwwowlolot.......',
    '.......ollooowsssowlowslot......',
    '......toolwwloppowlowslolot.....',
    '.....towmolsslopowswslowlo......',
    '......owmwolssloowsslowloot.....',
    '.....owmwwsolsslowssswlonpot....',
    '...itowmwsppolsowsllllopnpo.....',
    '..iiiowmwspppoowslooooppnpot....',
    '...itowmwsppoowsloooppppnso.i...',
    '.....owmwspoLLslosLaopppnsotii..',
    '....towmwwoLbbaooLbbaopsnso.i...',
    '.....towmoLbbbaooabbbaonso......',
    '......owoLbbbaoppoabbbaosot.....',
    '.......oLbbbaoppppoabbbaot......',
    '......oLbbbaoppppppoabbbaot.....',
    '.....toLbbaonpppppsnoabbao......',
    '......oaaaoppnnnnnnssoaaaot.....',
    '.......ooo.oopppsssootooot......',
    '........t...toooooo.t.t.t.......',
    '.............t.t.t..............',
    '................................',
    '................................',
    '................................',
    '................................',
  ],
);

export const bone = sprite('sym_bone', { o: 'ink', w: 'white', c: 'cream', s: 'sand', m: 'mauveGrey' }, [
  '................................',
  '................................',
  '.....................ooo........',
  '....................owcco.......',
  '...................owwwcco......',
  '..................ocwwccco......',
  '..................occcccsooo....',
  '...................occcswwcco...',
  '...................owwwcwwccso..',
  '..................owwwccwcccso..',
  '.................owwwccscccsmo..',
  '................owwwccsmccsso...',
  '...............owwwccsmoosmo....',
  '..............owwwccsmo..oo.....',
  '.............owwwccsmo..........',
  '............owwwccsmo...........',
  '...........owwwccsmo............',
  '..........owwwccsmo.............',
  '.....oo..owwwccsmo..............',
  '....owcoowwwccsmo...............',
  '...owwccwwwccsmo................',
  '..owwwccwwccsmo.................',
  '..occcccwccsmo..................',
  '..occccsccsmo...................',
  '...ossswwwcco...................',
  '....ooowwcccso..................',
  '......occccsmo..................',
  '......occcsso...................',
  '.......ossmo....................',
  '........ooo.....................',
  '................................',
  '................................',
]);

export const skull = sprite(
  'sym_skull',
  { o: 'ink', w: 'white', c: 'cream', s: 'sand', m: 'mauveGrey', n: 'night' },
  [
    '................................',
    '................................',
    '............oooooooo............',
    '..........oowwwwccccoo..........',
    '.........owwwwwwwccccco.........',
    '........owwwwwwwwcccccco........',
    '.......owwwwwwwwwccccccco.......',
    '.......owwwwwwwwwccccccco.......',
    '......owwwwwwwwwccccccccso......',
    '......owwwwwwwwwccccccccso......',
    '.....ocwwwwwwwwcccccccccsso.....',
    '.....occwwwwwccccccccccssso.....',
    '.....occccooooccccoooocssso.....',
    '.....occcooooooccoooooossmo.....',
    '.....occcooooooccoooooossmo.....',
    '......occooooooccoooooosmo......',
    '......occonooooccooooonsmo......',
    '......occconooccccooonsmso......',
    '......occccccccccsssssmsmo......',
    '.......occccccsoossssmsmo.......',
    '........oowssssoossmmcoo........',
    '........owwwccsssmccccso........',
    '........occoooooooooocso........',
    '........occwowowowowosso........',
    '.........occococococoso.........',
    '..........oooooooooooo..........',
    '...........oooooooooo...........',
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
  ],
);

export const eye = sprite(
  'sym_eye',
  {
    o: 'ink',
    w: 'white',
    r: 'red',
    s: 'salmonPink',
    p: 'pinkLilac',
    n: 'navy',
    S: 'sky',
    b: 'blue',
    c: 'cobalt',
    d: 'dustyPink',
    f: 'flesh',
    C: 'cerise',
  },
  [
    '................................',
    '................................',
    '................................',
    '............oooooo..............',
    '..........oowwwwwwoo............',
    '........oowwwwwwwwwwoo..........',
    '.......owwwwwwwwwwwwwro.........',
    '......owwwwwwwwwwwwrrwwo........',
    '.....owwwwwwwwwwwwwswwwpo.......',
    '.....orwwwwwnnnnnwwwwwwpo.......',
    '....owwswwwnSbbbSnwwwwwwpo......',
    '....orwwwwnSSbbbSbnwwwwppo......',
    '....owrrwnbbwwoobbbnwwwpro......',
    '....owwwrnbbwoooobcnwwrrpo......',
    '...owwwwwnbboooooccnwspppso.....',
    '....owwwwnSSoooooccnwwpppo......',
    '....owwwwnSbboooSccnwprrpo......',
    '....owwrswnbbbccccnwppppso......',
    '....orrwwwwnbccccnwppppsso......',
    '.....owrwwwwnnnnnwpppppso.......',
    '.....owwswwwwwwwppppppsdo.......',
    '......opwwwwwwpppprrpsdoo.......',
    '.......opppprppppppsrdoffo......',
    '........ooppprppppssoofffCo.....',
    '..........ooprssssoooofffCo.....',
    '............oooooo....offfo.....',
    '......................offfCo....',
    '.......................offfooo..',
    '........................offfsfo.',
    '.........................oCffCo.',
    '..........................oooo..',
    '................................',
  ],
);

export const tooth = sprite('sym_tooth', { o: 'ink', w: 'white', p: 'paper', k: 'khaki', s: 'sand' }, [
  '................................',
  '................................',
  '................................',
  '................................',
  '........oooooo....oooooo........',
  '.......owwwwwwo..owwwwwwo.......',
  '......owwwwwwwwoowwwwwwwwo......',
  '.....owwwwwwwwwpwwwwwwwwwpo.....',
  '.....owwwwwwwwwpwwwwwwwwwpo.....',
  '....owwwwwwwwwppwwwwwwwwppko....',
  '....owwwwwwwwppwwwwwwwwpppko....',
  '....owwwwwwwwpwwwwwwwwwppkso....',
  '....owwwwwwppwwwwwwwwpppkpko....',
  '....owwwwwwwwwwwwwwwwwwwppko....',
  '....owwwwwwwwwwwwwwwwwwppkso....',
  '.....owwwwppppppppppppppkko.....',
  '.....opppppppppppppppppkkso.....',
  '......opppkkkkkkkkkkkkkkso......',
  '.......owwwwpsssssswwwppo.......',
  '.......owwwwpkoooowwwwpko.......',
  '.......owwwwpo....owwwpko.......',
  '.......owwwwpo....owwwpko.......',
  '.......owwwwpo....owwwpko.......',
  '........owwwko....owwwpo........',
  '........owwpko....owwwpo........',
  '........owwpo......owwpo........',
  '........owwpo......owwpo........',
  '........owwko......owwko........',
  '.........oso........oso.........',
  '..........o..........o..........',
  '................................',
  '................................',
]);

export const goldtooth = sprite(
  'sym_goldtooth',
  { o: 'ink', g: 'gold', w: 'white', l: 'lemon', a: 'amber', c: 'copper' },
  [
    '................................',
    '................................',
    '................................',
    '....g......................g....',
    '...gwg..oooooo....oooooo..gwg...',
    '....g..ollllggo..ollllggo..g....',
    '......olllllgggoolllllgggo......',
    '.....ollllwlggggllllllggggo.....',
    '.....olllwwwlggglllllgggggo.....',
    '....ogllllwggggglllllgggggao....',
    '....oglllglggggllllgggggggao....',
    '....ogggggggggllllgggggggaco....',
    '....oglgggggggggggggggggagao....',
    '....oglllgggggggggggggggggao....',
    '....oggggggggggggggggggggaco....',
    '.....oggggggggggggggggggaao.....',
    '.....ogggggggggggggggggaaco.....',
    '......ogggaaaaaaaaaaaaaaco......',
    '.......ollgggcccccclggggo.......',
    '.......ollgggaooooglgggao.......',
    '.......oglgggo....olgggao.......',
    '.......oglgggo....olgggao.......',
    '.......oglgggo....ollggao.......',
    '........olggao....oglggo........',
    '........olggao....oglggo........',
    '........olggo......olggo........',
    '........olggo......olggo........',
    '........oggao......oggao........',
    '.........oco........oco.........',
    '..........o..........o..........',
    '................................',
    '................................',
  ],
);

export const halo = sprite(
  'sym_halo',
  { o: 'ink', l: 'lemon', g: 'gold', w: 'white', b: 'brass', a: 'amber' },
  [
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
    '...............l...........l....',
    '...............g..........lwl...',
    '....l..l.......g.......l...l....',
    '...lwl..g......g......g.........',
    '....l...g......o......g.........',
    '..l......ooooooloooooo......l...',
    '...g..ooollllllgggggggooo..g....',
    '.....owllgggbbbbbbbggggggo......',
    '....olggbbbbooooooobbbbgggo.....',
    '...olggboooo.......oooobgggo.g..',
    '..olgggo...............oggggo...',
    '..oggggoo.............ooggggo...',
    '..oaggglloooo.....oooollgggao...',
    '...oaggggllllooooollllggggao....',
    '....oaggggggglllllgggggggao.....',
    '.....oaawwggggggggggggaaao......',
    '......oooawaaaagaaaaaaooo.......',
    '.........ooooooaoooooo..........',
    '...............o................',
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
  ],
);

export const moon = sprite(
  'sym_moon',
  { o: 'ink', c: 'cobalt', w: 'white', l: 'lemon', b: 'blue', C: 'coral', p: 'pistachio', B: 'brass' },
  [
    '................................',
    '................................',
    '................................',
    '...........oooooo.....cccc......',
    '........ooowwwwllo......c.......',
    '.......owwwlllloo......c........',
    '......owllllllo.......cccc......',
    '.....owllllllo..................',
    '....owllllllo.............bbb...',
    '...owllllllo...............b....',
    '..owlllllllo..............bbb...',
    '..owllllllo.....................',
    '..owllllllo.....................',
    '.owllollolo.....................',
    '.owllloollo.....................',
    '.owllllllloo....................',
    '.owlllllllllo...................',
    '.owllCClllllo...................',
    '.olllllllllo....................',
    '..ollllllllo....................',
    '..olllllllllo...................',
    '..olllllollolo..................',
    '...ollllloolllo.................',
    '....olllllllllloo...............',
    '.....ollllllllpppoooo...........',
    '......oplllppppppppBBo..........',
    '.......oppppppppppBBo...........',
    '........oooppppBBooo............',
    '...........oooooo...............',
    '................................',
    '................................',
    '................................',
  ],
);

export const mirror = sprite(
  'sym_mirror',
  {
    o: 'ink',
    l: 'lemon',
    g: 'gold',
    c: 'coral',
    r: 'red',
    C: 'copper',
    a: 'amber',
    s: 'sky',
    w: 'white',
    i: 'ice',
    b: 'blue',
  },
  [
    '................................',
    '............o..oo..o............',
    '...........oloolgoolo...........',
    '..........olgggcrClgao..........',
    '..........ogaaoooogaCo..........',
    '.........olloosswwooggo.........',
    '.......oollossswwssiogaoo.......',
    '......olllossswwssissoaago......',
    '.....ollglosswwssisssoalgao.....',
    '.....oggaosswwssisssssogaCo.....',
    '......olloswwssissssssoaao......',
    '......oglowwssisssssssoaao......',
    '......ogowwssisssssssbboao......',
    '......oggossisssssssbboaCo......',
    '......oggosisssssssbbboaCo......',
    '.....ollgoisssssssbbbbollao.....',
    '.....oggagossssssbbbboaggao.....',
    '......oaagosssssbbbbboCCCo......',
    '.......ooaaosssbbbbboCCoo.......',
    '.........oaaoobbbbooCCo.........',
    '..........ollgoooollgo..........',
    '..........oggaCCCCggao..........',
    '...........oaooooooao...........',
    '............ooglgaoo............',
    '.............ollgao.............',
    '.............ollgao.............',
    '.............ollgao.............',
    '.............olggao.............',
    '.............oggaCo.............',
    '..............oooo..............',
    '................................',
    '................................',
  ],
);

export const morsel = sprite(
  'sym_morsel',
  { o: 'ink', a: 'apricot', b: 'bronze', c: 'cream', C: 'copper', B: 'brick', m: 'maroon' },
  [
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
    '...........oo.oo.oo.o...........',
    '.........ooaaoaaobbobo..........',
    '........ococcbacbbaCbboo........',
    '.......occbccbacbbaCbaCbo.......',
    '......oocccbccbcbaCbaCbbbo......',
    '.....ocbcccbcaaaabbbaCbbboo.....',
    '....occcbcccaaaabbbbbbbbaCCo....',
    '...oaccccbcaaaaabbbbbbbaCbbCo...',
    '...oCCcccaaaaaabbbbbbbaCbbCCo...',
    '...oBBCaaaaaaabbbbbbbbbbbCBBo...',
    '....omBCCaaaabbbbbbbbbbCCBBo....',
    '.....ooBmCCabbbbbbbbbCCBBoo.....',
    '.......oBBBCCCCCCCCCCBBmo.......',
    '........ooBBBBmmmBBmBBoo........',
    '..........oooommBmoooo..........',
    '..............oooo..............',
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
  ],
);

export const fork = sprite('sym_fork', { o: 'ink', w: 'white', s: 'steel', l: 'lilacGrey' }, [
  '................................',
  '................................',
  '...................o.o.o.o......',
  '..................owowososo.....',
  '.................owowososo......',
  '.................owowososo......',
  '................owowososo.......',
  '................owowososo.......',
  '...............owowososo........',
  '...............owowososo........',
  '..............owowososo.........',
  '..............owwwsssso.........',
  '.............owwsssslo..........',
  '..............owssslo...........',
  '..............owsloo............',
  '..............owso..............',
  '.............owso...............',
  '.............owso...............',
  '............owso................',
  '............owslo...............',
  '...........owslo................',
  '...........owslo................',
  '..........owslo.................',
  '.........owsslo.................',
  '........owsslo..................',
  '........owsslo..................',
  '.......owsslo...................',
  '.......owsslo...................',
  '......owsslo....................',
  '.......oslo.....................',
  '........oo......................',
  '................................',
]);

export const pepper = sprite(
  'sym_pepper',
  {
    o: 'ink',
    l: 'leaf',
    f: 'forest',
    c: 'clover',
    r: 'red',
    p: 'pine',
    b: 'blood',
    m: 'maroon',
    w: 'white',
    C: 'coral',
  },
  [
    '................................',
    '.....o..........................',
    '....oloo........................',
    '...ollllo.......................',
    '....ofcllo.oo...................',
    '.....ooclcorroooo...............',
    '.......ollcoorooroo.............',
    '.......oclcocollorro............',
    '......ooolcocccforrro...........',
    '......oolooccfoorrrrro..........',
    '......oocccccforrrrrrro.........',
    '......oofccffpcorrrrrrro........',
    '.......oolffpccorrrrrrro........',
    '........ocoooocporrrrrrbo.......',
    '.........oomoooobbrrrrrbo.......',
    '...........o....oorrrrrro.......',
    '..................orrrrrbo......',
    '...................orrrrbo......',
    '...................orrrrbo......',
    '...................orrrrmo......',
    '...................orrrbo.......',
    '...................orrrbo.......',
    '...................orrrbo.......',
    '...................orrbo........',
    '...................orrmo........',
    '..................owrbo.........',
    '..................orbo..........',
    '.................oCro...........',
    '.................obo............',
    '..................o.............',
    '................................',
    '................................',
  ],
);

export const appetizer = sprite(
  'sym_appetizer',
  {
    o: 'ink',
    f: 'flesh',
    c: 'cerise',
    s: 'sand',
    m: 'moss',
    p: 'pistachio',
    O: 'olive',
    r: 'red',
    b: 'blood',
    w: 'white',
    M: 'mud',
    B: 'brass',
    P: 'paper',
    a: 'amber',
    n: 'bronze',
    C: 'copper',
    S: 'steel',
  },
  [
    '................................',
    '...................o.o..........',
    '..................ofoco.........',
    '.................ofcfcfo........',
    '..................ocfco.........',
    '...................ooo..........',
    '..................oso...........',
    '..................oso...........',
    '..................oso...........',
    '.................oso............',
    '..............ooooso............',
    '.............ommmoso............',
    '............oppmmOo.............',
    '...........opppmmOOo............',
    '...........ommmmrrOo............',
    '...........ommmOrbOo............',
    '...........oOOOOOOOo............',
    '............oOOOOOo.............',
    '...........owoOOMowo............',
    '.........oowwwooowwwoo..........',
    '........oBoPPPPPPPPPoao.........',
    '........oBBoooooooooaaao........',
    '........onnnnnnnnnnnnnno........',
    '.....ooooCCCCCCCCCCCCCCoooo.....',
    '....owwwwoooooooooooooowwwwo....',
    '...oPPPPSSSSSSSSSSSSSSSSPPPPo...',
    '...oPPPPPPPPPPPPPPPPPPPPPPPPo...',
    '...oPPPPPPPPPPPPPPPPPPPPPPPPo...',
    '....ooSSSSSSSSSSSSSSSSSSSSoo....',
    '......ooooSSSSSSSSSSSSoooo......',
    '..........oooooooooooo..........',
    '................................',
  ],
);

export const main = sprite(
  'sym_main',
  {
    o: 'ink',
    l: 'lemon',
    a: 'amber',
    b: 'brass',
    c: 'copper',
    p: 'paper',
    s: 'steel',
    w: 'white',
    L: 'lilacGrey',
    r: 'red',
    B: 'blood',
  },
  [
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
    '................................',
    '..............oooo..............',
    '.............olllao.............',
    '.............ollbao.............',
    '.............obbaco.............',
    '.............oolboo.............',
    '..........ooopobaosooo..........',
    '.........oppwwpooppssso.........',
    '........owwwwwwpppppssso........',
    '.......owwwwwwwpppppsssso.......',
    '......owwwwwwwwpppppssssLo......',
    '.....opwwwwwwwwpppppssssLLo.....',
    '.....opwwwwwwwppppppssssLLo.....',
    '....oswwwwwwwssssssLLLLLLLLo....',
    '....osswwwwwsssssssLLLLLLLLo....',
    '....opppwwppppppppssssssLLLo....',
    '....oooooooooooooooooooooooo....',
    '....owwwwwwwwwssssssssssssso....',
    '...oLLLLLLLLLLLLLLLLLLLLLLLLo...',
    '..owooooLLLLLLLLLLLLLLLLoooowo..',
    '.oppppppooooooooooooorooppppppo.',
    '..oppppppppppppppppppBBppppppo..',
    '...ooossssssssssssssssssssooo...',
    '......oooooooooooooooooooo......',
    '................................',
    '................................',
  ],
);

export const dessert = sprite(
  'sym_dessert',
  {
    o: 'ink',
    f: 'forest',
    w: 'white',
    r: 'red',
    b: 'blood',
    p: 'pinkLilac',
    P: 'paper',
    s: 'salmonPink',
    c: 'cream',
    a: 'apricot',
  },
  [
    '................................',
    '................................',
    '.........................f......',
    '........................f.......',
    '.....................oof........',
    '....................owwro.......',
    '...................owwrrbo......',
    '...................orrrrbo......',
    '...................orrrbbo......',
    '...................oobbbo.......',
    '..................owwooowo......',
    '................ooowwwwwwo......',
    '..............oowwpoPPPPPo......',
    '............oowwppppooooopoo....',
    '..........oowwppppppppppppppo...',
    '........oowwppppppppppooooooo...',
    '......oowwppppppoooooosssssco...',
    '...oooppppoooooossssscccsccco...',
    '..osssoooossssssccccsccccccco...',
    '..ossssscccscccscccccccccrrro...',
    '..occcsccccscccccccrrrrrrraao...',
    '..occcccccccrrrrrrrrccccccaao...',
    '..occcrrrrrrrcccccccccccccaao...',
    '..orrrrcccccccccccccccccccaao...',
    '..occcccccccccccccccaaaaaaaao...',
    '..occccccccccaaaaaaaaaoooooo....',
    '..occccaaaaaaaaaoooooo..........',
    '..oaaaaaaaoooooo................',
    '...ooooooo......................',
    '................................',
    '................................',
    '................................',
  ],
);

export const plugPart5 = sprite(
  'sym_plug_part_5',
  {
    o: 'ink',
    b: 'brass',
    B: 'brick',
    l: 'lilacGrey',
    c: 'charcoal',
    d: 'dusk',
    n: 'night',
    L: 'lavender',
    p: 'plumGrey',
    w: 'white',
    C: 'copper',
  },
  [
    '................................',
    '................................',
    '................................',
    '......................oo........',
    '.....................obBo.......',
    '....................obBo........',
    '...............l..oobBo...o.....',
    '..............l.oocoBo...oBo....',
    '...............oddnco...obBo....',
    '..............odnnnnco.obBo.....',
    '.............odncnnnncobBo......',
    '............odncncnnnncoo.......',
    '............odcncncnnnnco.......',
    '............ocncncncnnco........',
    '............oocncncncnco........',
    '...........odnocncncnco.........',
    '..........ocnnnocncnco..........',
    '..........oocnnnoccco...........',
    '..........olocncoooo............',
    '.........olldoco................',
    '.........ollnoo.................',
    '.........oldno..................',
    '...L.....olpno..................',
    '..LwL....oldno..................',
    '...L...L.oldno..................',
    '.......ooolpno..................',
    '...b..olllldo...................',
    '....CClllldno...................',
    '...bboddpnno....................',
    '.....CCoooo.....................',
    '....C...........................',
    '................................',
  ],
);

export const handBone = sprite('hand_bone', { o: 'ink', c: 'cream', w: 'white', s: 'sand', m: 'mauveGrey' }, [
  '......occco..owcso......',
  '......occco..owcso......',
  '......occco..owcso......',
  '......occco..occso......',
  '......occcmo.occso......',
  '......occcso.occso......',
  '......occcso.occso......',
  '......occcso.occmo......',
  '......occcsoocccmo......',
  '......occcsooccco.......',
  '......occcsooccco.......',
  '......occcsooccco.......',
  '......occcsooccco.......',
  '.......occsooccco.......',
  '.......occsooccco.......',
  '.......occcooccco.......',
  '.......occcoowcco.......',
  '.......occcoowcco.......',
  '.......occcoowcco.......',
  '.......occcoowcco.......',
  '......owwwcoowwcco......',
  '......owwccsocccso......',
  '......occccmoccsmo......',
  '......oscssocoooo.......',
  '......ooooowccwwco......',
  '.....occccsccssccmo.....',
  '.....ooccsccswccso......',
  '....owwowwcsswcso.......',
  '...owwoocccscccmoo......',
  '..owwcowossoosmoco......',
  '.owccoocsoocooocco......',
  '.occsowcoocsocsocco.....',
  '.owsoowcoocsoccocco.....',
  '.ocmowccowcooccowcco....',
  'owcooccsowcocwcoccso....',
  'occocosooccmoccoosoo....',
  '.ocowco.oooooooo.occo...',
  '..oocso.ocso.ocsoocco...',
  '..owco.owco..occo.owco..',
  '..occo.owco..owcsoocso..',
  '..occo.occo..occmoocso..',
  '..occo.occo..owco.ocso..',
  '..owco.occo..owcoocwco..',
  '..occcoowwo..owcoowcso..',
  '...ooccoccso.ocsocsoo...',
  '.....ocooocoocmo.oo.....',
  '......o...o..oo.........',
  '........................',
]);

export const SYMBOL_SPRITES_E: SpriteDef[] = [
  hex,
  salt,
  sigLadder,
  sigWheel,
  sigTeeth,
  sigFeast,
  bone,
  skull,
  eye,
  tooth,
  goldtooth,
  halo,
  moon,
  mirror,
  morsel,
  fork,
  pepper,
  appetizer,
  main,
  dessert,
  plugPart5,
  handBone,
];
